import { Package, Percent, ShoppingCart, Calendar, Tag, FileText, List, ShoppingBag } from "lucide-react";
import { formatDateToBR } from "../../utils/date-utils";
import { formatPromotionText } from "../../utils/format-promotion-text";

interface CampaignPreviewHeaderProps {
  campaignType: 'PRO013' | 'PRO014' | 'PRO017';
  description?: string;
  startDate?: string;
  endDate?: string;
  takeQuantity?: number;
  payQuantity?: number;
  discountValue?: number;
  discountType?: number;
  listsCount?: number;
  productsCount?: number;
}

const CAMPAIGN_TYPE_INFO = {
  PRO013: {
    name: "Leve X Pague Y",
    icon: ShoppingCart,
  },
  PRO014: {
    name: "Leve X Pague Y (Itens)",
    icon: Package,
  },
  PRO017: {
    name: "Combo Moderno",
    icon: Tag,
  },
};

export function CampaignPreviewHeader({
  campaignType,
  description,
  startDate,
  endDate,
  takeQuantity,
  payQuantity,
  discountValue,
  discountType = 0,
  listsCount,
  productsCount,
}: CampaignPreviewHeaderProps) {
  const typeInfo = CAMPAIGN_TYPE_INFO[campaignType];
  const TypeIcon = typeInfo.icon;

  // Always show the preview header to display the campaign type

  return (
    <div className="mb-4 px-3 py-2 border rounded-lg bg-secondary/20">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {/* Tipo */}
        <span className="flex items-center gap-1">
          <TypeIcon className="h-3.5 w-3.5" />
          <span className="font-medium text-foreground">{typeInfo.name}</span>
        </span>

        {/* Vigência */}
        {(startDate || endDate) && (
          <>
            <span className="hidden sm:inline">|</span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {formatDateToBR(startDate)} - {formatDateToBR(endDate)}
            </span>
          </>
        )}

        {/* Descrição */}
        {description && (
          <>
            <span className="hidden sm:inline">|</span>
            <span className="flex items-center gap-1 max-w-[200px] truncate">
              <FileText className="h-3.5 w-3.5 shrink-0" />
              {description}
            </span>
          </>
        )}

        {/* Configuração - STORY-NEW-039: Texto formatado baseado no tipo de desconto */}
        {(takeQuantity && payQuantity) && (
          <>
            <span className="hidden sm:inline">|</span>
            <span className="flex items-center gap-1">
              <Tag className="h-3.5 w-3.5" />
              {formatPromotionText(takeQuantity, payQuantity, discountType)}
            </span>
          </>
        )}

        {/* Desconto */}
        {discountValue ? (
          <>
            <span className="hidden sm:inline">|</span>
            <span className="flex items-center gap-1 font-medium text-foreground">
              <Percent className="h-3.5 w-3.5" />
              {discountType === 0 ? `${discountValue}%` : `R$ ${discountValue.toFixed(2)}`}
            </span>
          </>
        ) : null}

        {/* Listas (PRO017) */}
        {listsCount !== undefined && listsCount > 0 && (
          <>
            <span className="hidden sm:inline">|</span>
            <span className="flex items-center gap-1">
              <List className="h-3.5 w-3.5" />
              {listsCount} lista{listsCount !== 1 ? 's' : ''}
            </span>
          </>
        )}

        {/* Produtos */}
        {productsCount !== undefined && productsCount > 0 && (
          <>
            <span className="hidden sm:inline">|</span>
            <span className="flex items-center gap-1">
              <ShoppingBag className="h-3.5 w-3.5" />
              {productsCount} produto{productsCount !== 1 ? 's' : ''}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
